import React, { useState } from 'react';
import { Booklet } from '../types';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, Copy, ExternalLink, ChevronRight, Palette, Droplets, Info } from 'lucide-react';

interface QRCodesProps {
  booklets: Booklet[];
}

const QRCodes: React.FC<QRCodesProps> = ({ booklets }) => {
  const [selectedId, setSelectedId] = useState<string | null>(booklets.length > 0 ? booklets[0].id : null);
  const [fgColor, setFgColor] = useState('#332f21'); // Default Brand Dark
  const [isTransparent, setIsTransparent] = useState(false);

  // Keep selection valid if booklets change
  React.useEffect(() => {
      if (selectedId && !booklets.find(b => b.id === selectedId) && booklets.length > 0) {
          setSelectedId(booklets[0].id);
      } else if (!selectedId && booklets.length > 0) {
          setSelectedId(booklets[0].id);
      }
  }, [booklets, selectedId]);

  const selectedBooklet = booklets.find(b => b.id === selectedId);
  
  // Robust URL Construction
  const getShareUrl = (id: string) => {
    const baseUrl = window.location.href.split('#')[0];
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}/#/view/${id}`;
  };

  const qrUrl = selectedBooklet ? getShareUrl(selectedBooklet.id) : '';

  const downloadQR = () => {
    try {
        const canvas = document.getElementById('high-res-qr') as HTMLCanvasElement;
        
        if (!canvas) {
            console.error("Canvas element not found");
            return;
        }

        const pngUrl = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        const safeTitle = selectedBooklet?.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'publication';
        downloadLink.download = `${safeTitle}_qr.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    } catch (error) {
        console.error("QR Download Error:", error);
    }
  };

  const colors = [
    '#332f21', // Dark (Brand)
    '#000000', // Black
    '#53565a', // Cool Gray (Brand)
    '#bfb8af', // Warm Gray (Brand)
    '#1d4ed8', // Blue
    '#b91c1c', // Red
    '#047857', // Green
  ];

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] w-full overflow-x-hidden">
      {/* Sidebar List - Mobile: Fixed height (35vh) to allow seeing preview below */}
      <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-warm/10 overflow-y-auto flex-shrink-0 bg-white h-[35vh] lg:h-auto">
        <div className="p-4 md:p-6 border-b border-warm/10 sticky top-0 bg-white z-10">
          <h1 className="font-serif text-xl md:text-2xl text-dark">QR Generator</h1>
          <p className="text-xs text-cool mt-1">Select a publication to generate code</p>
        </div>
        
        {booklets.length === 0 ? (
            <div className="p-8 text-center text-cool text-sm">No publications found.</div>
        ) : (
            <div className="divide-y divide-warm/10">
            {booklets.map(b => (
                <div 
                    key={b.id}
                    onClick={() => setSelectedId(b.id)}
                    className={`p-3 md:p-4 cursor-pointer transition-colors flex items-center justify-between group ${selectedId === b.id ? 'bg-warm/10' : 'hover:bg-gray-50'}`}
                >
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div className="w-8 h-10 md:w-10 md:h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0 border border-gray-100">
                            {b.coverUrl && <img src={b.coverUrl} className="w-full h-full object-cover" alt="" />}
                        </div>
                        <div className="min-w-0">
                            <h3 className={`font-semibold text-sm truncate ${selectedId === b.id ? 'text-dark' : 'text-cool'}`}>{b.title}</h3>
                            <p className="text-[10px] text-cool/60 truncate">{new Date(b.createdAt).toLocaleDateString()}</p>
                        </div>
                    </div>
                    {selectedId === b.id && <ChevronRight size={16} className="text-dark" />}
                </div>
            ))}
            </div>
        )}
      </div>

      {/* Main Preview Area */}
      <div className="flex-1 bg-[#f8f7f5] p-4 md:p-8 overflow-y-auto">
        {selectedBooklet ? (
            <div className="max-w-4xl mx-auto flex flex-col xl:flex-row gap-6 md:gap-8 items-start">
                
                {/* Visual Preview */}
                <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-warm/10 flex-1 w-full text-center">
                     <h2 className="font-serif text-lg md:text-xl text-dark mb-4 md:mb-6">{selectedBooklet.title}</h2>
                     
                     <div className={`flex justify-center mb-6 md:mb-8 p-4 md:p-6 border border-gray-100 rounded-xl shadow-inner transition-colors ${isTransparent ? 'bg-[url(https://bg.siteorigin.com/blog/wp-content/uploads/2015/06/patt01.png)]' : 'bg-white'}`}>
                        <QRCodeCanvas 
                            value={qrUrl}
                            size={250}
                            level={"H"}
                            includeMargin={!isTransparent}
                            fgColor={fgColor}
                            bgColor={isTransparent ? "transparent" : "#FFFFFF"}
                            imageSettings={{
                                src: "https://cdn-icons-png.flaticon.com/512/337/337946.png", 
                                x: undefined,
                                y: undefined,
                                height: 50,
                                width: 50,
                                excavate: true,
                            }}
                            className="w-48 h-48 md:w-64 md:h-64 max-w-full" // CSS Scaling for mobile
                        />
                    </div>

                    {/* Dynamic QR Code Explainer Box */}
                    <div className="w-full bg-blue-50/50 border border-blue-100 rounded-lg p-4 mb-6">
                      <div className="flex gap-3">
                        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-left">
                          <h4 className="text-sm font-semibold text-blue-900 mb-1">Dynamic QR Code Safe</h4>
                          <p className="text-xs text-blue-800 leading-relaxed">
                            This QR code is permanently locked to this specific publication slot. You can update the PDF file or change the branding at any time from your Dashboard, and this physical QR code will automatically load the newest version. 
                            <br/><br/>
                            <strong className="font-semibold text-blue-900">Warning:</strong> Never click "Delete" on this publication in your dashboard if this QR code has been printed on physical labels, as it will permanently break the link.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-left bg-gray-50 p-4 rounded-lg border border-gray-100 mb-6">
                        <p className="text-xs font-semibold text-cool uppercase mb-1">Target URL</p>
                        <div className="flex gap-2 items-stretch">
                             <input type="text" readOnly value={qrUrl} className="flex-1 min-w-0 bg-white border border-gray-200 text-xs px-2 py-1 rounded text-cool min-h-[44px]" />
                             <button onClick={() => navigator.clipboard.writeText(qrUrl)} className="text-dark hover:text-warm flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded transition-colors"><Copy size={16}/></button>
                        </div>
                    </div>

                    <button 
                        onClick={downloadQR}
                        className="w-full flex items-center justify-center gap-2 bg-dark text-white py-4 rounded-lg font-semibold hover:bg-cool transition-colors shadow-lg hover:shadow-xl active:scale-95 text-sm md:text-base min-h-[44px]"
                    >
                        <Download size={18} />
                        Download High-Res PNG
                    </button>
                </div>

                {/* Customization Controls */}
                <div className="w-full xl:w-80 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-warm/20">
                        <div className="flex items-center gap-2 mb-4 text-dark font-serif font-bold">
                            <Palette size={18} />
                            <h3>Customization</h3>
                        </div>

                        <div className="mb-6">
                            <label className="text-xs font-semibold text-cool uppercase tracking-wide block mb-3">QR Color</label>
                            <div className="flex flex-wrap gap-4 md:gap-3">
                                {colors.map(c => (
                                    <button
                                        key={c}
                                        onClick={() => setFgColor(c)}
                                        className={`w-11 h-11 md:w-8 md:h-8 rounded-full border-2 transition-all ${fgColor === c ? 'border-dark scale-110 shadow-md' : 'border-transparent hover:scale-110'}`}
                                        style={{ backgroundColor: c }}
                                    />
                                ))}
                                <div className="relative">
                                    <input 
                                        type="color" 
                                        value={fgColor}
                                        onChange={(e) => setFgColor(e.target.value)}
                                        className="w-11 h-11 md:w-8 md:h-8 opacity-0 absolute cursor-pointer"
                                    />
                                    <div className="w-11 h-11 md:w-8 md:h-8 rounded-full border border-gray-200 bg-gradient-to-tr from-yellow-400 via-red-500 to-blue-500 flex items-center justify-center pointer-events-none">
                                        <div className="w-8 h-8 md:w-6 md:h-6 rounded-full bg-white flex items-center justify-center text-[10px] md:text-[8px] text-dark font-bold">+</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="text-xs font-semibold text-cool uppercase tracking-wide block mb-3">Background</label>
                            <div 
                                onClick={() => setIsTransparent(!isTransparent)}
                                className="flex items-center justify-between p-3 min-h-[44px] rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <Droplets size={18} className={isTransparent ? 'text-blue-500' : 'text-gray-400'} />
                                    <span className="text-sm font-medium text-dark">Transparent Background</span>
                                </div>
                                <div className={`w-10 h-5 rounded-full relative transition-colors ${isTransparent ? 'bg-dark' : 'bg-gray-300'}`}>
                                    <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${isTransparent ? 'left-6' : 'left-1'}`} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* HIDDEN HIGH RES CANVAS */}
                <div style={{ position: 'fixed', top: '-10000px', left: '-10000px', visibility: 'hidden' }}>
                    <QRCodeCanvas 
                        id="high-res-qr"
                        value={qrUrl}
                        size={2000} // High Resolution
                        level={"H"}
                        includeMargin={!isTransparent}
                        fgColor={fgColor}
                        bgColor={isTransparent ? "transparent" : "#FFFFFF"}
                    />
                </div>
            </div>
        ) : (
            <div className="h-full flex flex-col items-center justify-center text-cool opacity-50">
                <p>Select a publication from the list to begin.</p>
            </div>
        )}
      </div>
    </div>
  );
};

export default QRCodes;